/**
 * Branch-card document helpers: build the iframe srcDoc that renders one
 * branch's content, and capture edited content back out.
 *
 * Branch content is the slot element's WHOLE element (outerHTML, marker
 * stripped) — the content root IS the element, so the document is that element
 * alone and capture serializes the root itself. What the element loses by being
 * lifted out of the email is restored around it, as preview-only rules the card
 * injects: the email's font definitions and stylesheet (`email-head-styles`),
 * the styling it inherited from its ancestors (`element-context-styles`), and
 * the width it rendered at. Pure functions, shared by the branch cards and the
 * edit-tab hooks.
 */
import {
  TABLE_SECTION_TAGS,
  wrapTableFragment,
} from "src/components/preview-canvas/table-fragment";

import {
  contentRootTagName,
  DYNAMIC_SLOT_ATTRIBUTE,
  stripEditorArtifacts,
} from "./types";

/** Default/minimum desktop viewport width. The canvas widens per email when
 * its own media queries demand it — see `computeDesktopPreviewWidth`. */
export const EMAIL_WIDTH = 640;

/** Editor-only marker for the content root inside a branch editing doc.
 * Never persisted: capture strips it from the serialized root. */
export const BRANCH_ROOT_ATTRIBUTE = "data-ht-branch-root";

function wrapFragment(
  elementHtml: string,
  layoutWidth: number,
  headHtml: string,
): string {
  return `<!DOCTYPE html><html><head>${headHtml}</head><body style="margin:0;width:${layoutWidth}px">${wrapTableFragment(elementHtml)}</body></html>`;
}

/**
 * Parse an element's outerHTML in a tag-appropriate context. A bare
 * `<td>`/`<tr>`/`<tbody>` parsed inside a `<div>` gets dropped by the HTML
 * parser (children hoisted), which would lose the root. Table-section roots
 * are reachable: a multi-selection spanning rows promotes to their `<tbody>`.
 */
function parseRootElement(html: string): Element | null {
  const tag = contentRootTagName(html);
  if (!tag) return null;
  // The wrapper carries a parse-only attribute so the root is found as its
  // first child — selecting by tag would match a same-tag wrapper instead.
  const wrapped =
    tag === "td" || tag === "th"
      ? `<table><tbody><tr data-ht-parse-wrapper>${html}</tr></tbody></table>`
      : tag === "tr"
        ? `<table><tbody data-ht-parse-wrapper>${html}</tbody></table>`
        : TABLE_SECTION_TAGS.has(tag)
          ? `<table data-ht-parse-wrapper>${html}</table>`
          : `<div data-ht-parse-wrapper>${html}</div>`;
  const doc = new DOMParser().parseFromString(wrapped, "text/html");
  return (
    doc.querySelector("[data-ht-parse-wrapper]")?.firstElementChild ?? null
  );
}

/**
 * The content root for a branch: this variant's content, parsed and marked.
 * `fallbackTagName` (the slot element's tag) supplies a structurally valid root
 * when the content is empty or rootless — a hardcoded `<div>` would be captured
 * as real content on first edit and could be invalid where the element sits
 * (e.g. a `td` slot).
 */
function buildContentRoot(
  content: string,
  fallbackTagName: string,
  editable: boolean,
): Element {
  // Content with no root element — empty, or legacy/malformed content that's a
  // bare text node — gets one, so the card still shows something editable.
  const rooted = contentRootTagName(content)
    ? content
    : `<${fallbackTagName}>${content}</${fallbackTagName}>`;
  // The fallback is reachable, not just defensive: `contentRootTagName` is a
  // regex, so it can report root tags the parser then refuses to nest in any
  // of `parseRootElement`'s contexts (e.g. `<caption>`/`<colgroup>`). Such a
  // card renders the fallback element rather than nothing.
  const el =
    parseRootElement(rooted) ?? document.createElement(fallbackTagName);
  // Stale markers (this slot's or others', left by earlier sessions) would
  // masquerade as live slots and leak the editor's slot outline — drop them.
  el.removeAttribute(DYNAMIC_SLOT_ATTRIBUTE);
  el.querySelectorAll(`[${DYNAMIC_SLOT_ATTRIBUTE}]`).forEach((nested) =>
    nested.removeAttribute(DYNAMIC_SLOT_ATTRIBUTE),
  );
  if (editable) {
    el.setAttribute(BRANCH_ROOT_ATTRIBUTE, "true");
  }
  return el;
}

/**
 * Render one branch's content as the document's root element. `elementHtml` (the
 * slot element's outerHTML from the email version) supplies the root tag when
 * content is empty. When `editable`, the root carries BRANCH_ROOT_ATTRIBUTE so
 * edits can be captured back out; static previews drop it entirely.
 *
 * `layoutWidth` should be the element's measured rendered width in the email so
 * its content wraps the same way (defaults to full email width), and `headHtml`
 * carries the email's font `<link>`s (see `extractPreviewFontLinks`) so typefaces
 * resolve. The email's own CSS is NOT baked in — it depends on the previewed
 * viewport, so the frame injects `extractPreviewCss` into the live document.
 */
export function buildBranchSrcDoc(
  elementHtml: string,
  content: string,
  {
    editable,
    layoutWidth = EMAIL_WIDTH,
    headHtml = "",
  }: { editable: boolean; layoutWidth?: number; headHtml?: string },
): string {
  const root = buildContentRoot(
    content,
    contentRootTagName(elementHtml) ?? "div",
    editable,
  );
  return wrapFragment(root.outerHTML, layoutWidth, headHtml);
}

/**
 * Read the edited branch content back out of an editing doc: the root
 * element's outerHTML with the editor-only attributes stripped. The wrapper
 * scaffolding (table/tr shells, body, injected styles) is structurally
 * excluded; the root's own attributes and styling are real content.
 */
export function captureBranchContentHtml(doc: Document): string | null {
  const root = doc.querySelector(`[${BRANCH_ROOT_ATTRIBUTE}]`);
  if (!root) return null;
  const clone = root.cloneNode(true) as Element;
  clone.removeAttribute(BRANCH_ROOT_ATTRIBUTE);
  clone.removeAttribute(DYNAMIC_SLOT_ATTRIBUTE);
  return stripEditorArtifacts(clone.outerHTML);
}
